/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import SweetAlert from 'react-bootstrap-sweetalert';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui icons
import Check from '@material-ui/icons/Check';
// @material-ui/core components
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// our components
import adCreateStyles from 'assets/jss/AdsCreate';
import { PROMOTED_AD } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import InfoUser from 'components/common/InfoUser';
import Loading from 'components/common/Loading';
import UserSelect from 'components/User/Select';
import Datepicker from 'components/common/DatePicker';
import Moment from 'react-moment';
import AuthContext from 'helpers/authContext';
import WithPermission from 'components/User/WithPermission';
import { leads, userRanks, paidAdCurrency } from 'config';
import FormControl from '@material-ui/core/FormControl';
import { allowPaidAdChangeCurrency, isUserPublic } from 'helpers/functions';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */

const translateValidationSchema = t =>
  Yup.object({
    ad: Yup.string().required(t('errors.required')),
    fromOwner: Yup.boolean(),
    owner: Yup.object()
      .nullable()
      .when('fromOwner', {
        is: false,
        then: Yup.object()
          .nullable()
          .required(),
      }),
    package: Yup.object({
      id: Yup.string().required(),
    }),
    free: Yup.bool(),
  });

const ReadableDate = ({ years, months, days, hours, minutes, seconds }) =>
  moment()
    .add({ seconds, minutes, hours, days, months, years })
    .fromNow(true);

class PromotedAdUpdate extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    const { promotedAd, packages, ad } = props;
    const promotedAdPackage = packages.find(
      p =>
        p.locationScope === promotedAd.locationScope &&
        ReadableDate(p.duration) === ReadableDate(promotedAd.duration),
    );

    this.state = {
      fields: {
        ad: ad || promotedAd.ad.id,
        date: moment(promotedAd.startDate),
        package:
          ad.toString() === promotedAd.ad.id.toString()
            ? promotedAdPackage
            : undefined,
        agency: promotedAd.manager,
        owner: promotedAd.owner,
        fromOwner: promotedAd.manager === null,
        free: false,
        lead: promotedAd.lead,
        currency: promotedAd.currency,
        secondaryCurrency: promotedAd.secondaryCurrency,
      },
      showSelectAgency: false,
      showSelectOwner: false,
      createdFromManager: promotedAd.manager !== null,
      exchangeRate: null,
    };
    this.formikRef = null;
    moment.locale(props.i18n.language);
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(PROMOTED_AD.LIST);
  };

  /**
   * @description
   * Updating before send to parent.
   *
   * @param {*} values - New values to update fields.
   * @returns {*} Undefined - No returns.
   */
  handleSubmit = values => {
    const { onSave, promotedAd } = this.props;

    const finalValues = {
      id: promotedAd.id,
      ad: values.ad,
      package: values.package.id,
      owner: isUserPublic(values.owner) ? values.owner.id : undefined,
      free: values.free,
      startDate: values.date.format() || null,
      secondaryCurrency: values.secondaryCurrency,
    };
    if (promotedAd.online)
      return onSave(
        {
          id: finalValues.id,
          ad: finalValues.ad,
          package: finalValues.package,
        },
        'online',
      );
    if (promotedAd.manager)
      return onSave({ ...finalValues, lead: values.lead }, 'fromAgency');
    return onSave(finalValues, 'fromOwner');
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   *
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    const { t } = this.props;
    let all = null;
    if (serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
        if (item.code === 'no-packages') {
          all = [t('promoted_ad.form.no_packages')];
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  render() {
    const {
      classes,
      errors: serverErrors,
      submitLoading,
      t,
      promotedAd,
      exchangeRates,
      onLoadPackages,
      onResetPackages,
      packages,
    } = this.props;
    const originalSecondaryCurrency = promotedAd.secondaryCurrency;
    const originalExchangeRate =
      promotedAd.userPrice > 0 && promotedAd.secondaryUserPrice > 0
        ? promotedAd.secondaryUserPrice / promotedAd.userPrice
        : null;
    const {
      showSelectOwner,
      showSelectAgency,
      createdFromManager,
      fields,
      exchangeRate,
    } = this.state;

    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);

    // if the paid ad was created Online  or
    // is running (the lastRenewal has value) we can edit just the ID
    const isEditionForbidden =
      promotedAd.online ||
      promotedAd.consumedCredit !== null ||
      promotedAd.hasPromotion ||
      promotedAd.promoType;
    return (
      <AuthContext.Consumer>
        {/* eslint-disable-next-line consistent-return */}
        {user => {
          const hasGeneral =
            user.allPermissions.filter(
              permission => permission.codename === 'change_promotedad',
            ).length === 1;
          return (
            <React.Fragment>
              <Snackbars
                place="tc"
                message={all || ''}
                open={!!all}
                color="danger"
                close
              />

              <Formik
                ref={ref => {
                  this.formikRef = ref;
                }}
                initialValues={fields}
                validationSchema={validationSchema}
                onSubmit={values => this.handleSubmit(values)}
                render={({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleSubmit,
                  handleBlur,
                  resetForm,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div className={classes.pageContainer}>
                      <div
                        className={`${classes.row} ${classes.contentBtns}`}
                        style={{ justifyContent: 'space-between' }}
                      >
                        <h3>{t('promoted_ad.form.edit')}</h3>
                      </div>

                      <Card>
                        <CardBody>
                          <Loading show={submitLoading} />
                          {createdFromManager &&
                            (hasGeneral || user.rank === userRanks.ADMIN) && (
                              <FormControl
                                fullWidth
                                className={classes.userSelect}
                              >
                                <h6> {t('userAgency.lead')} </h6>
                                <GridContainer>
                                  <GridItem xs={12} sm={6}>
                                    <Button
                                      data-cy="sellerLead"
                                      id="seller-lead"
                                      name="sellerLead"
                                      simple={values.lead !== leads.SELLER}
                                      round
                                      fullWidth
                                      color="info"
                                      disabled={isEditionForbidden}
                                      onClick={() => {
                                        handleChange({
                                          target: {
                                            name: 'lead',
                                            value: leads.SELLER,
                                          },
                                        });
                                      }}
                                    >
                                      Seller
                                    </Button>
                                  </GridItem>
                                  <GridItem xs={12} sm={6}>
                                    {' '}
                                    <Button
                                      data-cy="revoLead"
                                      id="revo-lead"
                                      name="revoLead"
                                      simple={values.lead !== leads.REVOLICO}
                                      round
                                      fullWidth
                                      disabled={isEditionForbidden}
                                      color="info"
                                      onClick={() => {
                                        handleChange({
                                          target: {
                                            name: 'lead',
                                            value: leads.REVOLICO,
                                          },
                                        });
                                      }}
                                    >
                                      Revolico
                                    </Button>
                                  </GridItem>
                                </GridContainer>
                              </FormControl>
                            )}
                          {createdFromManager &&
                            !values.fromOwner &&
                            (hasGeneral || user.rank === userRanks.ADMIN) && (
                              <SweetAlert
                                title=""
                                showConfirm={false}
                                showCancel={false}
                                onConfirm={() => {}}
                                customClass={classes.adModalConfirmation}
                                show={showSelectAgency}
                                closeOnClickOutside
                                onCancel={() =>
                                  this.setState({
                                    showSelectAgency: !showSelectAgency,
                                  })
                                }
                              >
                                <UserSelect
                                  listAgencies
                                  selectUser={agency => {
                                    handleChange({
                                      target: {
                                        name: 'agency',
                                        value: agency,
                                      },
                                    });
                                    // reset user owner
                                    if (
                                      !values.agency ||
                                      (values.agency &&
                                        agency.id !== values.agency.id)
                                    ) {
                                      handleChange({
                                        target: {
                                          name: 'owner',
                                          value: null,
                                        },
                                      });
                                    }
                                    this.setState({
                                      showSelectAgency: !showSelectAgency,
                                    });
                                  }}
                                />
                              </SweetAlert>
                            )}
                          <React.Fragment>
                            <div className={classes.section}>
                              {values.agency && !values.fromOwner && (
                                <h6>{t('promoted_ad.form.fields.agency')}</h6>
                              )}
                              <div className={classes.categorySection}>
                                {values.agency && !values.fromOwner && (
                                  <InfoUser user={values.agency} />
                                )}
                                {!isEditionForbidden &&
                                  !values.fromOwner &&
                                  createdFromManager &&
                                  (hasGeneral ||
                                    user.rank === userRanks.ADMIN) && (
                                    <React.Fragment>
                                      <Button
                                        simple
                                        color="primary"
                                        onClick={() =>
                                          this.setState({
                                            showSelectAgency: true,
                                          })
                                        }
                                      >
                                        {values.agency
                                          ? t('ad.form.fields.change')
                                          : t('ad.form.fields.select')}
                                      </Button>
                                      {isEditionForbidden &&
                                        !values.fromOwner &&
                                        values.agency && (
                                          <Button
                                            simple
                                            color="primary"
                                            onClick={() => {
                                              handleChange({
                                                target: {
                                                  name: 'agency',
                                                  value: null,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'owner',
                                                  value: null,
                                                },
                                              });
                                            }}
                                          >
                                            {t('promoted_ad.form.clean')}
                                          </Button>
                                        )}
                                    </React.Fragment>
                                  )}
                              </div>
                            </div>
                          </React.Fragment>
                          {/* show owner selection modal  */}
                          {!isEditionForbidden && !values.fromOwner && (
                            <SweetAlert
                              title=""
                              showConfirm={false}
                              showCancel={false}
                              onConfirm={() => {}}
                              customClass={classes.adModalConfirmation}
                              show={showSelectOwner}
                              closeOnClickOutside
                              onCancel={() =>
                                this.setState({
                                  showSelectOwner: !showSelectOwner,
                                })
                              }
                            >
                              {values.agency ? (
                                <UserSelect
                                  agency={values.agency}
                                  selectUser={owner => {
                                    handleChange({
                                      target: {
                                        name: 'owner',
                                        value: owner,
                                      },
                                    });
                                    this.setState({
                                      showSelectOwner: !showSelectOwner,
                                    });
                                  }}
                                />
                              ) : (
                                <UserSelect
                                  listUserAgencies
                                  selectUser={owner => {
                                    handleChange({
                                      target: {
                                        name: 'owner',
                                        value: owner,
                                      },
                                    });
                                    this.setState({
                                      showSelectOwner: !showSelectOwner,
                                    });
                                  }}
                                />
                              )}
                            </SweetAlert>
                          )}
                          <React.Fragment>
                            <div
                              className={classes.section}
                              style={{ marginBottom: 32 }}
                            >
                              <h6
                                style={{
                                  color:
                                    touched.owner && errors.owner
                                      ? 'red'
                                      : 'black',
                                }}
                              >
                                {t('promoted_ad.form.fields.owner')}
                              </h6>
                              <div className={classes.row}>
                                {isUserPublic(values.owner) && (
                                  <InfoUser user={values.owner} />
                                )}
                                {values.owner &&
                                  !isUserPublic(values.owner) &&
                                  `${t(
                                    'promoted_ad.form.fields.private_user',
                                  )} (${values.owner.fakeId})`}
                                {!isEditionForbidden && !values.fromOwner && (
                                  <React.Fragment>
                                    {values.owner &&
                                      ((!createdFromManager && hasGeneral) ||
                                        createdFromManager ||
                                        user.rank === userRanks.ADMIN) && (
                                        <Button
                                          simple
                                          color="primary"
                                          id="selectOwner"
                                          onClick={() =>
                                            this.setState({
                                              showSelectOwner: true,
                                            })
                                          }
                                        >
                                          {t('ad.form.fields.change')}
                                        </Button>
                                      )}
                                    {!values.owner && !values.fromOwner && (
                                      <Button
                                        simple
                                        color="primary"
                                        id="selectOwner"
                                        onClick={() =>
                                          this.setState({
                                            showSelectOwner: true,
                                          })
                                        }
                                      >
                                        {t('ad.form.fields.select')}
                                      </Button>
                                    )}
                                    {!isEditionForbidden &&
                                      values.owner &&
                                      !values.fromOwner &&
                                      ((!createdFromManager && hasGeneral) ||
                                        createdFromManager ||
                                        user.rank === userRanks.ADMIN) && (
                                        <Button
                                          simple
                                          color="primary"
                                          onClick={() => {
                                            handleChange({
                                              target: {
                                                name: 'owner',
                                                value: null,
                                              },
                                            });
                                          }}
                                        >
                                          {t('promoted_ad.form.clean')}
                                        </Button>
                                      )}
                                  </React.Fragment>
                                )}
                                {touched.owner && errors.owner && (
                                  <p className={classes.errorCategoryField}>
                                    {t('errors.required')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </React.Fragment>

                          <GridContainer>
                            <GridItem xs={12} sm={6}>
                              <div
                                style={{
                                  marginBottom: 20,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'stretch',
                                }}
                              >
                                <TextField
                                  label={t('promoted_ad.form.fields.id')}
                                  name="ad"
                                  error={touched.ad && !!errors.ad}
                                  helperText={touched.ad ? errors.ad : ''}
                                  InputProps={{
                                    value: values.ad,
                                    onChange: e => {
                                      resetForm();
                                      onResetPackages();
                                      handleChange(e);
                                    },
                                    onBlur: handleBlur,
                                    type: 'number',
                                  }}
                                />
                                <Button
                                  id="load-packages"
                                  color="primary"
                                  simple
                                  onClick={() => {
                                    handleChange({
                                      target: {
                                        name: 'package',
                                        value: null,
                                      },
                                    });
                                    onLoadPackages(
                                      values.ad,
                                      values.fromOwner ? user : values.owner,
                                    );
                                  }}
                                >
                                  {t('promoted_ad.form.button_packages')}
                                </Button>
                              </div>
                            </GridItem>
                            {packages.length > 0 && (
                              <GridItem xs={12} sm={6}>
                                {isEditionForbidden ? (
                                  <React.Fragment>
                                    <h6>{t('promoted_ad.form.fields.date')}</h6>
                                    <Moment format="LLLL" date={values.date} />
                                  </React.Fragment>
                                ) : (
                                  <Datepicker
                                    title={t('promoted_ad.form.fields.date')}
                                    defaultValue=""
                                    required
                                    fromToday
                                    dateTime
                                    onChange={date => {
                                      handleChange({
                                        target: {
                                          name: 'date',
                                          value: date || '',
                                        },
                                      });
                                    }}
                                    value={values.date}
                                  />
                                )}
                              </GridItem>
                            )}
                          </GridContainer>
                          {packages.length > 0 && (
                            <div className={classes.section}>
                              <h6
                                style={{
                                  color:
                                    touched.package &&
                                    errors.package &&
                                    (!values.package ||
                                      !values.package.locationScope)
                                      ? 'red'
                                      : 'black',
                                }}
                              >
                                {t('promoted_ad.form.fields.locationScope')}
                              </h6>
                              {packages
                                .map(p => p.locationScope)
                                .filter(
                                  (locationScope, index, self) =>
                                    self.indexOf(locationScope) === index,
                                )
                                .map((locationScope, key) => (
                                  <Button
                                    key={`locationScope-${locationScope}`}
                                    simple={
                                      values.package
                                        ? locationScope !==
                                          values.package.locationScope
                                        : true
                                    }
                                    round
                                    disabled={
                                      isEditionForbidden &&
                                      !packages
                                        .filter(
                                          p =>
                                            p.locationScope === locationScope,
                                        )
                                        .some(
                                          p =>
                                            (promotedAd.promoType ===
                                              'CREDIT_BUNDLE' &&
                                              JSON.stringify(
                                                promotedAd.duration,
                                              ) ===
                                                JSON.stringify(p.duration)) ||
                                            (promotedAd.promoType !==
                                              'CREDIT_BUNDLE' &&
                                              p.price ===
                                                promotedAd.package.price),
                                        )
                                    }
                                    id={`locationScope-${key}`}
                                    color="info"
                                    onClick={() => {
                                      handleChange({
                                        target: {
                                          name: 'package',
                                          value: {
                                            locationScope,
                                          },
                                        },
                                      });
                                    }}
                                  >
                                    {t(
                                      // eslint-disable-next-line max-len
                                      `promoted_ad.form.fields.${locationScope.toLowerCase()}`,
                                    )}
                                  </Button>
                                ))}
                            </div>
                          )}
                          {packages.length > 0 && values.package && (
                            <>
                              <div className={classes.section}>
                                <h6
                                  style={{
                                    color:
                                      touched.package &&
                                      errors.package &&
                                      (!values.package || !values.package.id)
                                        ? 'red'
                                        : 'black',
                                  }}
                                >
                                  {t('promoted_ad.form.fields.duration')}
                                </h6>

                                {packages
                                  .filter(
                                    p =>
                                      p.locationScope ===
                                      values.package.locationScope,
                                  )
                                  .map((p, key) => (
                                    <Button
                                      key={`duration-${ReadableDate(
                                        p.duration,
                                      )}`}
                                      id={`duration-${key}`}
                                      simple={
                                        values.package
                                          ? p.id !== values.package.id
                                          : true
                                      }
                                      round
                                      disabled={
                                        isEditionForbidden &&
                                        !(
                                          (promotedAd.promoType ===
                                            'CREDIT_BUNDLE' &&
                                            JSON.stringify(
                                              promotedAd.duration,
                                            ) === JSON.stringify(p.duration)) ||
                                          (promotedAd.promoType !==
                                            'CREDIT_BUNDLE' &&
                                            p.price ===
                                              promotedAd.package.price)
                                        )
                                      }
                                      color="info"
                                      onClick={() => {
                                        handleChange({
                                          target: {
                                            name: 'package',
                                            value: p,
                                          },
                                        });
                                      }}
                                    >
                                      {ReadableDate(p.duration)}
                                    </Button>
                                  ))}
                              </div>
                              <div className={classes.section}>
                                <div>
                                  <WithPermission
                                    allowPermissionFunc={
                                      allowPaidAdChangeCurrency
                                    }
                                  >
                                    <div className={classes.section}>
                                      <h6>
                                        {t('promoted_ad.form.fields.currency')}
                                      </h6>
                                      {Object.keys(paidAdCurrency).map(
                                        (currency, key) => {
                                          const rate = exchangeRates.find(
                                            value =>
                                              value.currencyFrom ===
                                                fields.currency &&
                                              value.currencyTo === currency,
                                          );
                                          if (
                                            fields.currency !== currency &&
                                            !rate
                                          )
                                            return null;

                                          return (
                                            <Button
                                              key={`currency-${currency}`}
                                              simple={
                                                fields.secondaryCurrency
                                                  ? fields.secondaryCurrency !==
                                                    currency
                                                  : fields.currency !== currency
                                              }
                                              round
                                              id={`currency-${key}`}
                                              color="info"
                                              disabled={isEditionForbidden}
                                              onClick={() => {
                                                handleChange({
                                                  target: {
                                                    name: 'secondaryCurrency',
                                                    value:
                                                      fields.currency !==
                                                        currency && currency,
                                                  },
                                                });
                                                this.setState({
                                                  fields: {
                                                    ...fields,
                                                    secondaryCurrency:
                                                      fields.currency !==
                                                        currency && currency,
                                                  },
                                                  exchangeRate:
                                                    fields.currency !==
                                                      currency && rate.rate,
                                                });
                                              }}
                                            >
                                              {currency}
                                            </Button>
                                          );
                                        },
                                      )}
                                    </div>
                                  </WithPermission>
                                </div>
                                <div>
                                  <h6>{t('promoted_ad.form.fields.price')}</h6>
                                  {values.free ? (
                                    <p>
                                      {`0 ${values.secondaryCurrency ||
                                        values.currency}`}
                                    </p>
                                  ) : isEditionForbidden &&
                                    !promotedAd.promoType ? (
                                    values.package.price && (
                                      <p>
                                        {!values.secondaryCurrency &&
                                          `${values.package.price} ${values.currency}`}
                                        {values.secondaryCurrency &&
                                          values.secondaryCurrency ===
                                            originalSecondaryCurrency &&
                                          `${values.package.price *
                                            originalExchangeRate} ${
                                            values.secondaryCurrency
                                          }`}
                                        {values.secondaryCurrency &&
                                          values.secondaryCurrency !==
                                            originalSecondaryCurrency &&
                                          exchangeRate &&
                                          `${values.package.price *
                                            exchangeRate} ${
                                            values.secondaryCurrency
                                          }`}
                                      </p>
                                    )
                                  ) : promotedAd.promoType ===
                                    'CREDIT_BUNDLE' ? (
                                    `0 ${values.currency}`
                                  ) : (
                                    values.package.finalPrice && (
                                      <p>
                                        {!values.secondaryCurrency &&
                                          `${values.package.finalPrice.price} ${values.currency}`}
                                        {values.secondaryCurrency &&
                                          values.secondaryCurrency ===
                                            originalSecondaryCurrency &&
                                          `${values.package.finalPrice.price *
                                            originalExchangeRate} ${
                                            values.secondaryCurrency
                                          }`}
                                        {values.secondaryCurrency &&
                                          values.secondaryCurrency !==
                                            originalSecondaryCurrency &&
                                          exchangeRate &&
                                          `${values.package.finalPrice.price *
                                            exchangeRate} ${
                                            values.secondaryCurrency
                                          }`}
                                      </p>
                                    )
                                  )}
                                </div>
                                <div>
                                  <h6>
                                    {t('promoted_ad.form.fields.discounts')}
                                  </h6>
                                  {(!isEditionForbidden ||
                                    promotedAd.promoType) &&
                                    values.package.finalPrice &&
                                    values.package.finalPrice.discounts.map(
                                      discount => (
                                        <p>
                                          {t(
                                            // eslint-disable-next-line max-len
                                            `promoted_ad.form.fields.discountTypes.${discount}`,
                                          )}
                                        </p>
                                      ),
                                    )}
                                </div>
                              </div>
                            </>
                          )}
                          {packages.length > 0 && values.package && (
                            <WithPermission
                              allowPermissions={[
                                'make_free_promotedad',
                                'make_free_ownership_promotedad',
                              ]}
                            >
                              {!isEditionForbidden && (
                                <div style={{ marginTop: 15 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        onClick={e => {
                                          handleChange(e);
                                        }}
                                        checked={values.free}
                                        name="free"
                                        checkedIcon={
                                          <Check
                                            className={classes.checkedIcon}
                                          />
                                        }
                                        icon={
                                          <Check
                                            className={classes.uncheckedIcon}
                                          />
                                        }
                                        classes={{
                                          checked: classes.checked,
                                        }}
                                      />
                                    }
                                    label={t('promoted_ad.form.fields.free')}
                                  />
                                </div>
                              )}
                            </WithPermission>
                          )}
                          <div className={classes.section}>
                            <GridContainer
                              direction="row-reverse"
                              style={{
                                justifyContent:
                                  packages.length > 0
                                    ? 'space-between'
                                    : 'center',
                              }}
                            >
                              {packages.length > 0 && (
                                <GridItem xs={12} sm={6}>
                                  <div
                                    className={classes.contentBtns}
                                    style={{
                                      flex: 'None',
                                    }}
                                  >
                                    <Button
                                      id="save-ad"
                                      color="primary"
                                      type="submit"
                                    >
                                      {t('promoted_ad.form.button_edit')}
                                    </Button>
                                  </div>
                                </GridItem>
                              )}
                              <GridItem
                                xs={12}
                                sm={6}
                                style={{
                                  flex: 'None',
                                }}
                              >
                                <Button
                                  id="cancel-update"
                                  simple
                                  color="danger"
                                  onClick={this.handleCancelBtn}
                                >
                                  {t('common.cancel')}
                                </Button>
                              </GridItem>
                            </GridContainer>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </form>
                )}
              />
            </React.Fragment>
          );
        }}
      </AuthContext.Consumer>
    );
  }
}

/**
 * AdForm PropTypes
 * @param classes - styles received from withStyles
 * @param ad - Entity data
 * @param editMode - edition mode
 * @param errors - Errors data list
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with language information
 * @param exchangeRateQuery - Query for get an ExchangeRate
 * @callback onSave - callback called when user clicks save button
 * @callback onLoadPackages - callback when user clicks load packages button
 * @callback onResetPackages - callback when user clicks reset packages button
 * @param packages - Packages data list
 */

PromotedAdUpdate.propTypes = {
  promotedAd: PropTypes.instanceOf(Object).isRequired,
  ad: PropTypes.instanceOf(Object).isRequired,
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onLoadPackages: PropTypes.func.isRequired,
  onResetPackages: PropTypes.func.isRequired,
  packages: PropTypes.instanceOf(Array).isRequired,
  exchangeRates: PropTypes.instanceOf(Array).isRequired,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PromotedAdUpdateWrapper = props => <PromotedAdUpdate {...props} />;

export default withTranslation()(
  withStyles(adCreateStyles)(PromotedAdUpdateWrapper),
);
